'use client';

import { useState } from "react";
import Link from "next/link";
import LoginModal from "./LoginModal";
import { useAuth } from "@/app/context/AuthContext";

interface MenuItem {
  label: string;
  href?: string;
  icon?: React.ReactNode;
  children?: MenuItem[];
}

// 图标组件定义在组件外部
const PartsProcessingIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="25" height="25" className="flex-shrink-0">
    <circle cx="12" cy="12" r="8" fill="none" stroke="black" strokeWidth="2"></circle>
    <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.22 4.22l1.41 1.41M18.36 18.36l1.41 1.41M4.22 19.78l1.41-1.41M18.36 5.64l1.41-1.41" fill="none" stroke="black" strokeWidth="2"></path>
    <circle cx="12" cy="12" r="3" fill="none" stroke="black" strokeWidth="2"></circle>
  </svg>
);

const FigureIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="25" height="25" className="flex-shrink-0">
    <ellipse cx="12" cy="20" rx="8" ry="2" fill="none" stroke="black" strokeWidth="2"></ellipse>
    <path d="M11 12 Q 12 6, 13 12 L 13 16 L 11 16 Z" fill="none" stroke="black" strokeWidth="2"></path>
    <circle cx="12" cy="8" r="2" fill="none" stroke="black" strokeWidth="2"></circle>
    <path d="M10 12 C 8 10, 8 12, 10 14" fill="none" stroke="black" strokeWidth="2"></path>
    <path d="M14 12 C 16 10, 16 12, 14 14" fill="none" stroke="black" strokeWidth="2"></path>
    <path d="M11 16 Q 12 18, 13 16" fill="none" stroke="black" strokeWidth="1"></path>
  </svg>
);

const ServiceIndustryIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="25" height="25" className="flex-shrink-0">
    <circle cx="12" cy="8" r="4" fill="none" stroke="black" strokeWidth="2"></circle>
    <path d="M8 8 C 8 4, 16 4, 16 8" fill="none" stroke="black" strokeWidth="2"></path>
    <path d="M6 12 C 6 10, 18 10, 18 12" fill="none" stroke="black" strokeWidth="2"></path>
    <line x1="6" y1="12" x2="6" y2="16" stroke="black" strokeWidth="2"></line>
    <line x1="18" y1="12" x2="18" y2="16" stroke="black" strokeWidth="2"></line>
    <path d="M12 16 C 12 20, 8 20, 8 16" fill="none" stroke="black" strokeWidth="2"></path>
    <path d="M12 16 C 12 20, 16 20, 16 16" fill="none" stroke="black" strokeWidth="2"></path>
  </svg>
);

const MyAccountIcon = () => (
  <svg width="25" height="25" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
    <rect x="2" y="2" width="20" height="20" rx="5" stroke="black" strokeWidth="2"></rect>
    <circle cx="12" cy="8" r="3" stroke="black" strokeWidth="2"></circle>
    <path d="M6 20C6 16.6863 8.68629 14 12 14C15.3137 14 18 16.6863 18 20" stroke="black" strokeWidth="2"></path>
  </svg>
);

const AboutUsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="25" height="25" className="flex-shrink-0">
    <path d="M4 9C4 8.45 4.45 8 5 8H7C7.55 8 8 8.45 8 9V12C8 12.55 7.55 13 7 13H5C4.45 13 4 12.55 4 12V9Z" fill="none" stroke="black" strokeWidth="2"></path>
    <path d="M6 11V10H5L4 9L5 8H6V7H7L8 8H9L8 9L7 10H6V11Z" fill="none" stroke="black" strokeWidth="2"></path>
    <path d="M14 9C14 8.45 14.45 8 15 8H17C17.55 8 18 8.45 18 9V12C18 12.55 17.55 13 17 13H15C14.45 13 14 12.55 14 12V9Z" fill="none" stroke="black" strokeWidth="2"></path>
    <path d="M16 11V10H15L14 9L15 8H16V7H17L18 8H19L18 9L17 10H16V11Z" fill="none" stroke="black" strokeWidth="2"></path>
    <path d="M4 12C4 13 5 14 6 14H7C8 14 9 13 9 12V10H8V12C8 13 9 14 10 14H11C12 14 13 13 13 12V10H12V12C12 13 13 14 14 14H15C16 14 17 13 17 12V9" fill="none" stroke="black" strokeWidth="2"></path>
  </svg>
);

const ChevronIcon = ({ isExpanded }: { isExpanded: boolean }) => (
  <svg 
    focusable="false" 
    width="12" 
    height="8" 
    className={`icon icon--chevron transition-transform ${isExpanded ? 'rotate-180' : ''}`}
    viewBox="0 0 12 8"
  >
    <path fill="none" d="M1 1l5 5 5-5" stroke="currentColor" strokeWidth="1"></path>
  </svg>
);

export default function Sidebar() {
  const [expandedItems, setExpandedItems] = useState<string[]>(['My Account']);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const { isAuthenticated } = useAuth();
  
  const isLoggedIn = isAuthenticated;

  const toggleExpanded = (label: string) => {
    setExpandedItems(prev => 
      prev.includes(label) 
        ? prev.filter(item => item !== label)
        : [...prev, label]
    );
  };

  const handleAccountClick = (e: React.MouseEvent, href: string) => {
    if (!isLoggedIn && href.startsWith('/account/')) {
      e.preventDefault();
      setLoginModalOpen(true);
    }
  };

  const menuItems: MenuItem[] = [
    {
      label: 'Services',
      icon: <PartsProcessingIcon />,
      children: [
        { label: 'CNC Machining', href: '/services/cnc-machining' },
        { label: 'Sheet Metal', href: '/services/sheet-metal-fabrication' },
        { label: 'Injection Molding', href: '/services/injection-molding' },
        { label: '3D Printing', href: '/services/3d-printing' },
        { label: 'PCB / PCBA', href: '/services/pcb-pcba' },
        { label: 'LiB Consultancy', href: '/services/vacuum-casting' },
      ]
    },
    // {
    //   label: '3D Figures',
    //   href: '/products/3d-figures',
    //   icon: <FigureIcon />
    // },
    {
      label: 'Industries',
      icon: <ServiceIndustryIcon />,
      children: [
        { label: 'Electronics', href: '/industries/electronics' },
        { label: 'Aerospace', href: '/industries/aerospace' },
        { label: 'Automotive', href: '/industries/automotive' },
        { label: 'Consumer Products', href: '/industries/consumer-products' },
        { label: 'Pharmaceutical', href: '/industries/pharmaceutical' },
        { label: 'Industrial', href: '/industries/industrial' },
      ]
    },
    {
      label: 'My Account',
      icon: <MyAccountIcon />,
      children: [
        { label: 'Profile', href: '/account/myaccount' },
        { label: 'Project Hub', href: '/account/orders' },
        { label: 'Shopping Cart', href: '/account/mycart' },
      ]
    },
    {
      label: 'About Us',
      icon: <AboutUsIcon />,
      children: [
        { label: 'Our Story', href: '/company/our-story' },
        // { label: 'Investor Relations', href: '/company/our-investor' },
        { label: 'Contact Us', href: '/company/contact-us' },
      ]
    },
  ];

  return (
    <aside className={`menu-container ${sidebarExpanded ? '' : 'menu-container--collapsed'}`}>
      {/* Logo */}
      <div className="sidebar-logo-row">
        <div className="logo">
          <Link href="/">
            <img src="/logo.png" alt="Fabdigit" />
          </Link>
        </div>
        <button
          type="button"
          className="sidebar-menu-btn"
          aria-label={sidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          aria-expanded={sidebarExpanded}
          // onClick={() => setSidebarExpanded((v) => !v)}
        >
          <img src="/menu.png" alt="" />
        </button>
      </div>

      {sidebarExpanded ? (
        <Link
          href="/services/cnc-machining/"
          className="rapid-quote-btn"
          style={{
            display: 'block',
            margin: '20px',
            padding: '10px 16px',
            backgroundColor: '#C00000',
            color: '#fff',
            textAlign: 'center',
            borderRadius: '25vw',
            textDecoration: 'none',
            fontSize: '.912em',
            fontWeight: 'bold',
          }}
        >
          Rapid Quote
        </Link>
      ) : null}

      {/* Navigation Menu */}
      {sidebarExpanded ? (
        <nav>
          <ul className="menu-links">
            {menuItems.map((item) => (
              <li key={item.label}>
                {item.children ? (
                  <>
                    <a
                      href="javascript:void(0);"
                      onClick={(e) => {
                        e.preventDefault();
                        toggleExpanded(item.label);
                      }}
                    >
                      <span className="flex items-center gap-2">
                        <span>{item.label}</span>
                      </span>
                      <span className="svg">
                        <ChevronIcon isExpanded={expandedItems.includes(item.label)} />
                      </span>
                    </a>
                    {expandedItems.includes(item.label) && item.children && (
                      <div className="dropdown" style={{ display: 'block' }}>
                        {item.children.map((child) => (
                          <Link
                            key={child.label}
                            href={child.href || '#'}
                            onClick={(e) => handleAccountClick(e, child.href || '')}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link href={item.href || '#'}>
                    <span className="flex items-center gap-2">
                      <span>{item.label}</span>
                    </span>
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </nav>
      ) : null}
      
      {/* Login Modal */}
      <LoginModal isOpen={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
    </aside>
  );
}
